// Cliente de la telemedicina en la Sala del HCE (plan Sala CRM↔HCE, rebanada 8b; PLAN 4.10) — ver packages/web/src/api/http-routes/hce-telemedicina.ts y
// docs/telemedicina-sala-backend.md.
//
// Reglas que este archivo respeta (las mismas de salaHce.ts):
//   - el consultorio y el médico los decide el SERVIDOR por la sesión: aquí nunca se manda un id de médico ni de consultorio;
//   - toda acción lleva el estado que la pantalla VIO (`estadoEsperado`): si cambió, el servidor responde 409 y la pantalla vuelve a pedir la lista;
//   - los plazos («ya se puede marcar No asistió», «ya toca el aviso») se deciden con la hora del SERVIDOR (`ahora` de cada respuesta), nunca con la
//     hora de pared del equipo del médico: se mide con un reloj monotónico lo que pasó desde que llegó la respuesta (`ahoraServidorMs`).
import { ErrorSala, pedir } from './salaHce';

export type EstadoVirtual = 'programada' | 'en_consulta';

/** Una cita virtual del médico (`FilaTelemedicina` del servidor). */
export type CitaVirtual = {
  citaId: string;
  virtual: true;
  estado: EstadoVirtual;
  etiqueta: 'Programada' | 'En consulta' | 'Atendida (orden enviada)';
  ordenEnviada: boolean;
  /** Hora agendada (ISO 8601 UTC). */
  inicio: string;
  fin: string | null;
  apareceDesde: string;
  conectable: boolean;
  pacienteNombre: string;
  specialtyId: string | null;
  motivo: string | null;
  ticketId: number | null;
  encounterId: string | null;
  practitionerId: string;
  /** El servidor recibió «la videollamada terminó» (Digital Samba) con el médico en consulta: solo una pista para sugerir «Finalizar». */
  sesionTerminada: string | null;
};

export type ListaVirtuales = { ahora: string; citas: CitaVirtual[] };

export type ResultadoConectarVirtual = { ok: true; estado: 'en_consulta'; link: string; ticketId: number; cambio: boolean; encounterId: string | null };
export type ResultadoFinalizarVirtual =
  | { ok: true; estado: 'atendida'; ticketId: number; encounterCerrado: boolean | 'pendiente' }
  | { ok: false; requiereConfirmacion: true; pendientes: number; claves: string[] };
export type ResultadoNoAsistioVirtual = { ok: true; estado: 'no_asistio'; detalle: string };

export const obtenerVirtualesDelDia = (): Promise<ListaVirtuales> => pedir('GET', '/api/hce/telemedicina/citas');
export const obtenerVirtualesProximas = (): Promise<ListaVirtuales> => pedir('GET', '/api/hce/telemedicina/proximas');
export const conectarVirtual = (citaId: string, estadoEsperado: EstadoVirtual): Promise<ResultadoConectarVirtual> =>
  pedir('POST', `/api/hce/telemedicina/${encodeURIComponent(citaId)}/conectar`, { estadoEsperado });
export const finalizarVirtual = (citaId: string, estadoEsperado: EstadoVirtual, confirmar = false): Promise<ResultadoFinalizarVirtual> =>
  pedir('POST', `/api/hce/telemedicina/${encodeURIComponent(citaId)}/finalizar`, { estadoEsperado, ...(confirmar ? { confirmar: true } : {}) });
export const noAsistioVirtual = (citaId: string, estadoEsperado: EstadoVirtual): Promise<ResultadoNoAsistioVirtual> =>
  pedir('POST', `/api/hce/telemedicina/${encodeURIComponent(citaId)}/no-asistio`, { estadoEsperado });

// --- Lógica pura (se prueba sin red ni DOM) -----------------------------------------------------------------------------------------------

/**
 * Hora del servidor ahora mismo (ms epoch): lo que dijo en `ahora` + lo transcurrido DESDE que llegó la respuesta, medido con el reloj monotónico
 * (`recibidoEnMs` y `monotonicoMs` son lecturas del mismo reloj, p. ej. `performance.now()`). Si `ahora` no es una fecha válida devuelve `null`: quien
 * llama no decide nada con un reloj que no conoce.
 */
export function ahoraServidorMs(ahoraIso: string, recibidoEnMs: number, monotonicoMs: number): number | null {
  const base = Date.parse(ahoraIso);
  if (!Number.isFinite(base)) return null;
  return base + Math.max(0, monotonicoMs - recibidoEnMs);
}

/** «No asistió» en consulta (el médico se conectó y el paciente no entró): el servidor lo permite pasada la hora + 10 min (GRACIA_NO_ASISTIO_EN_CONSULTA_MS). */
export const GRACIA_NO_ASISTIO_EN_CONSULTA_MS = 10 * 60_000;

export type AccionVirtual = 'conectar' | 'volverAEntrar' | 'finalizar' | 'noAsistio';

/**
 * Qué botones ofrece una cita virtual (el servidor lo vuelve a exigir). Una virtual NO usa Llamar ni «Iniciar consulta» (PLAN 4.10).
 *   - programada: «Conectar» y, pasada la hora de la cita, «No asistió»;
 *   - en consulta: «Volver a entrar» (enlace nuevo, sin cambiar nada), «Finalizar consulta» y, si no hubo atención y pasó hora + 10 min, «No asistió».
 * `ahoraSrvMs = null` (reloj del servidor desconocido): no se ofrece «No asistió» (el servidor responde con el plazo si se intenta).
 */
export function accionesDeVirtual(c: Pick<CitaVirtual, 'estado' | 'inicio' | 'ordenEnviada'>, ahoraSrvMs: number | null): AccionVirtual[] {
  const inicio = Date.parse(c.inicio);
  if (c.estado === 'programada') {
    return ['conectar', ...(ahoraSrvMs !== null && Number.isFinite(inicio) && ahoraSrvMs >= inicio ? (['noAsistio'] as const) : [])];
  }
  const puedeNoAsistio = !c.ordenEnviada && ahoraSrvMs !== null && Number.isFinite(inicio) && ahoraSrvMs >= inicio + GRACIA_NO_ASISTIO_EN_CONSULTA_MS;
  return ['volverAEntrar', 'finalizar', ...(puedeNoAsistio ? (['noAsistio'] as const) : [])];
}

/** Fecha corta (`dd/mm/aaaa`) en el reloj de Ecuador, sin depender de la zona horaria del equipo. */
export function fechaEcuadorDe(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'America/Guayaquil' });
}

// --- «Conectar»: abre la videollamada ------------------------------------------------------------------------------------------------------

/** Lo mínimo de `window.open` que se usa (se inyecta en las pruebas). */
export type AbridorDeVentanas = (url: string, destino: string) => { location: { href: string }; opener: unknown; close: () => void } | null;

export type ResultadoConectarYAbrir = { link: string; abierta: boolean; resultado: ResultadoConectarVirtual };

/**
 * «Conectar»: marca la conexión en el servidor y abre el enlace de moderador en una pestaña nueva.
 *
 * El navegador solo permite abrir una ventana en respuesta directa a un clic; la petición al servidor tarda, así que la ventana se abre EN BLANCO
 * ANTES de pedir el enlace y después se le pone la dirección (con `opener = null`, para que la sala no pueda tocar esta pestaña). Si el servidor
 * rechaza (409, 503…) la ventana en blanco se cierra y el error sube. Si el navegador bloquea la ventana (`abierta: false`), el enlace se devuelve
 * igual: la pantalla lo muestra como un enlace para pulsar (nunca se pierde la conexión ya marcada).
 */
export async function conectarYAbrir(
  citaId: string,
  estadoEsperado: EstadoVirtual,
  abrir: AbridorDeVentanas | null = typeof window === 'undefined' ? null : (url, destino) => window.open(url, destino),
  conectar: typeof conectarVirtual = conectarVirtual,
): Promise<ResultadoConectarYAbrir> {
  let ventana: ReturnType<AbridorDeVentanas> = null;
  try {
    ventana = abrir ? abrir('about:blank', '_blank') : null;
  } catch {
    ventana = null;
  }
  let resultado: ResultadoConectarVirtual;
  try {
    resultado = await conectar(citaId, estadoEsperado);
  } catch (err) {
    try {
      ventana?.close();
    } catch {
      // ya cerrada
    }
    throw err instanceof ErrorSala ? err : new ErrorSala('No se pudo conectar.', 0, 'error');
  }
  // El enlace lo da nuestro servidor, pero solo se navega a una dirección https (nunca javascript: ni data:).
  if (!/^https:\/\//i.test(resultado.link)) {
    try {
      ventana?.close();
    } catch {
      // ya cerrada
    }
    throw new ErrorSala('El servidor devolvió un enlace de videollamada no válido.', 0, 'enlace_invalido');
  }
  if (!ventana) return { link: resultado.link, abierta: false, resultado };
  try {
    ventana.opener = null;
    ventana.location.href = resultado.link;
    return { link: resultado.link, abierta: true, resultado };
  } catch {
    return { link: resultado.link, abierta: false, resultado };
  }
}
